import clsx from "clsx";
import { Plus, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { basisLabel, fraction, fromNet, rateFor, toNet, toNumber } from "../../shared/money";
import { AddPicker } from "../../web/components/AddPicker";
import { TextField } from "../../web/components/fields";
import { SyncBadge } from "../../web/components/SyncBadge";
import { WindowPopup } from "../../web/components/WindowPopup";
import { Box, ErrorNotice, IconButton, SectionCard } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { can, costIncludesTax, itemName, kitsWith, usePriceListSaver } from "./data";
import { matchesSearch } from "./ListToolbar";
import type { KitPart, MaterialRow, PriceListData } from "./meta";
import { partItem } from "./pricing";
import { usePriceListWindows } from "./windows";

/*
 * A kit's parts, on its Parts tab (kits.ts): each part (opens it), its cost, how many and that times how many, in the terms costs
 * are shown in (Price List settings), each at its own rate; under them, the total before tax and with it. Over them, a search of its
 * parts and Add item (as a group's Items tab has): a popup in the window, a search with an Add on each
 * (single items, not kits). Their number is changed in place, or they're taken out. The kit's cost
 * never changes by itself: when they give another, it shows "Cost to update" (on its Cost section).
 */
export function KitParts({ data, item }: Readonly<{ data: PriceListData; item: MaterialRow }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const windows = usePriceListWindows();
    const [error, setError] = useState<Error | null>(null);
    // Searching its parts (name, item number, full name); the Add popup open.
    const [filter, setFilter] = useState("");
    const [adding, setAdding] = useState(false);
    const parts = data.kitParts[item.uuid] ?? [];
    const canEdit = can(data, "costs.edit");
    const byUuid = new Map(data.materials.map((m) => [m.uuid, m]));
    const save = (next: KitPart[]) => {
        setError(null);
        return saver.setKitParts(item.uuid, next).catch((err: Error) => {
            setError(err);
            throw err;
        });
    };
    // Each part's cost as costs are shown everywhere (Price List settings: with tax, or without when it's claimed on purchases),
    // each at its own rate (a part without tax gets none added): so the list adds up to its totals, under it.
    const inc = costIncludesTax(data);
    const terms = basisLabel(inc, rateFor("", data.taxRates));
    const rateOf = (m: MaterialRow) => fraction(rateFor(m.tax_rate_uuid, data.taxRates));
    // Each part's own cost (pricing.ts partItem: not the labour in it).
    const netOf = (m: MaterialRow) => toNet(toNumber(partItem(data, m).cost), m.price_includes_taxes, rateOf(m));
    const eachOf = (m: MaterialRow) => fromNet(netOf(m), inc, rateOf(m));
    // Its totals: before tax and with it (each part's at its own rate), whichever costs are shown in.
    const counted = parts.flatMap((p) => {
        const m = byUuid.get(p.partUuid);
        return m ? [{ net: netOf(m) * p.quantity, gross: fromNet(netOf(m), true, rateOf(m)) * p.quantity }] : [];
    });
    const totalNet = counted.reduce((sum, c) => sum + c.net, 0);
    const totalGross = counted.reduce((sum, c) => sum + c.gross, 0);
    const defaultRate = rateFor("", data.taxRates);
    // Single items not in it yet (a kit's parts aren't kits), active.
    const choices = data.materials
        .filter((m) => m.active && m.uuid !== item.uuid && !(m.uuid in data.kitParts) && !parts.some((p) => p.partUuid === m.uuid))
        .map((m) => ({ value: m.uuid, label: itemName(data, m), synced: m.synced, detail: m.item_number || undefined }));
    // Those matching the search (a part no longer there matches none but an empty search).
    const shown = parts.filter((p) => {
        const m = byUuid.get(p.partUuid);
        return m ? matchesSearch(`${itemName(data, m)} ${m.item_number} ${m.name}`, filter) : !filter.trim();
    });
    // One grid for its header, rows and totals, so they line up (the same whatever the quantities): the part, each, how many,
    // that times how many, and taking it out.
    const columns = "grid grid-cols-[minmax(0,1fr)_5.5rem_4rem_5.5rem_2rem] items-center gap-x-3 px-3";
    const money = "text-right tabular-nums";

    return (
        <Box>
            <SectionCard
                label="Parts"
                aside="Its cost is theirs added up."
                bodyClassName=""
                // Searching its parts and adding some (a popup in the window: a search with an Add on each), along its top.
                header={
                    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-2 px-3 py-2">
                        <label className="relative block min-w-0 flex-1">
                            <span className="sr-only">Search this kit's parts</span>
                            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                            <input className="input pl-9" placeholder="Search its parts" value={filter} onChange={(e) => setFilter(e.target.value)} />
                        </label>
                        {canEdit ? (
                            <button type="button" className="btn-secondary shrink-0" onClick={() => setAdding(true)}>
                                <Plus className="size-4" /> Add item
                            </button>
                        ) : null}
                    </div>
                }
            >
                {adding ? (
                    <WindowPopup top title={`Add parts to ${itemName(data, item)}`} onClose={() => setAdding(false)}>
                        <AddPicker
                            inline
                            placeholder="Search by name or item number"
                            options={choices}
                            onAdd={(uuid) => save([...parts, { partUuid: uuid, quantity: 1 }])}
                            onUndo={(uuid) => save(parts.filter((p) => p.partUuid !== uuid))}
                        />
                    </WindowPopup>
                ) : null}
                {error ? (
                    <div className="p-3">
                        <ErrorNotice error={error} />
                    </div>
                ) : null}
                {parts.length === 0 ? <p className="m-0 p-3 text-sm text-muted">No parts yet: add what it&apos;s made of, and its cost becomes theirs added up.</p> : null}
                {parts.length > 0 && shown.length === 0 ? <p className="m-0 p-3 text-sm text-muted">No parts match.</p> : null}
                {shown.length ? (
                    <div>
                        <div className={clsx(columns, "border-b border-line bg-surface-2 py-1.5 text-xs font-medium text-muted")}>
                            <span>Part</span>
                            <span className="text-right" title={`Each part's cost, ${terms} (as costs are shown: Price List settings)`}>
                                Each ({terms})
                            </span>
                            <span className="text-center">Qty</span>
                            <span className="text-right">Line</span>
                            <span aria-hidden />
                        </div>
                        <ul className="divide-y divide-line">
                            {shown.map((p) => {
                                const m = byUuid.get(p.partUuid);
                                return (
                                    <li key={p.partUuid} className={clsx(columns, "py-1 text-sm")}>
                                        <span className="flex min-w-0 items-center gap-2">
                                            {m ? <SyncBadge synced={m.synced} /> : null}
                                            <button type="button" className="min-w-0 cursor-pointer truncate text-left hover:underline" disabled={!m} onClick={() => windows.openItem(p.partUuid)}>
                                                {m ? itemName(data, m) : "An item that no longer exists"}
                                            </button>
                                            {m?.item_number ? <span className="shrink-0 text-xs text-muted">{m.item_number}</span> : null}
                                            {m && !m.active ? <span className="shrink-0 text-xs text-muted">archived</span> : null}
                                        </span>
                                        <span className={money}>{m ? fmt.money(eachOf(m)) : ""}</span>
                                        <TextField
                                            label={m ? `How many ${itemName(data, m)}` : "How many"}
                                            hideLabel
                                            value={String(p.quantity)}
                                            inputMode="decimal"
                                            disabled={!canEdit}
                                            validate={(v) => (Number(v) > 0 ? null : "Above 0")}
                                            onSave={(v) => save(parts.map((x) => (x.partUuid === p.partUuid ? { ...x, quantity: Number(v) } : x)))}
                                            className="[&_.input]:py-1 [&_.input]:text-center"
                                        />
                                        <span className={money}>{m ? fmt.money(eachOf(m) * p.quantity) : ""}</span>
                                        {canEdit ? (
                                            <IconButton label="Take it out of the kit" onClick={() => void save(parts.filter((x) => x.partUuid !== p.partUuid)).catch(() => undefined)}>
                                                <Trash2 className="size-4" />
                                            </IconButton>
                                        ) : (
                                            <span aria-hidden />
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                        {/* What they add up to, before tax and with it, under the Line figures. Its stock is its own: said in its Details' Stock. */}
                        <div className="border-t border-line bg-surface-2 py-1.5 text-sm" title="Each part's cost, times how many, added up (each with its own tax)">
                            <div className={columns}>
                                <span className="col-span-3 font-medium">Total {basisLabel(false, defaultRate)}</span>
                                <strong className={money}>{fmt.money(totalNet)}</strong>
                            </div>
                            <div className={clsx(columns, "text-muted")}>
                                <span className="col-span-3">Total {basisLabel(true, defaultRate)}</span>
                                <span className={money}>{fmt.money(totalGross)}</span>
                            </div>
                        </div>
                    </div>
                ) : null}
            </SectionCard>
        </Box>
    );
}

/** On a part's Costing tab: the kits it's in (each opens), so changing its cost is known to reach them. Nothing when none. */
export function PartOfKits({ data, item }: Readonly<{ data: PriceListData; item: MaterialRow }>) {
    const windows = usePriceListWindows();
    const kits = kitsWith(data, item.uuid);
    if (!kits.length) return null;
    return (
        <p className="m-0 text-sm text-muted">
            <span className="font-medium text-ink">Used in {kits.length === 1 ? "a kit" : `${kits.length} kits`}:</span>{" "}
            {kits.map((k, i) => (
                <span key={k.uuid}>
                    {i ? ", " : null}
                    <button type="button" className="link-button" onClick={() => windows.openItem(k.uuid)}>
                        {itemName(data, k)}
                    </button>
                </span>
            ))}
            . Changing its cost shows on {kits.length === 1 ? "it" : "them"} as a cost to update.
        </p>
    );
}
